/**
 * TimelineTotalsBadge — compact per-series total block for timeline charts
 * (v1.4 dashboard-ux-fix).
 *
 * Motivation (user report 2026-04-23): "на каждом timeline-графике в правом
 * верхнем углу добавить блок с тоталами по категориям, чтобы сразу видеть
 * сколько всего объектов каждой категории в графике". Timeline charts in v2
 * show 2–3 series over buckets; reading "how many sent/failed/skipped in the
 * window" required hovering every bucket or reading the KPI row — neither
 * presents the per-series total at a glance.
 *
 * Contract:
 *   - `items` is an ordered array of `{ key, label, color, value, format? }`.
 *   - `value` of `null` / `undefined` renders as an em-dash, and that em-dash
 *     means ONE thing: this series cannot be summed (an average, a percentage
 *     — pass `null` rather than a pre-summed number).
 *   - `loading` is the OTHER absence, and it must not borrow that em-dash:
 *     "not counted yet" is not "not summable", and since the recount rule
 *     (hooks/use-filtered-query.ts) it happens on every filter change rather
 *     than once per session. It renders a skeleton the size of a value.
 *   - `format` is the formatter hint ('number' | 'currency' | 'hours').
 *     Defaults to 'number' with thousands separators.
 *   - Totals block sits inside the chart Card's header row via
 *     `flex items-center justify-between` on the CardHeader. Callers own the
 *     flex layout; the badge stays minimal.
 *   - Responsive: on mobile (<sm) the badge is allowed to wrap under the
 *     title via `flex-wrap` + `gap-y` on the parent. We don't hide it —
 *     hiding the numbers defeats the purpose — but individual callers can
 *     apply `hidden sm:flex` if a specific chart header gets too cramped.
 *
 * Visual: a horizontal row of pill-like entries, each
 * `<swatch><label>:<value>` at `text-xs` weight. Colour swatches match the
 * series stroke/fill so the reader can map the number back to the line.
 */

import * as React from 'react';

import { cn } from '@/lib/utils';

const NUM_FMT = new Intl.NumberFormat('en-US');
const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

export type TimelineTotalFormat = 'number' | 'currency' | 'hours';

export interface TimelineTotalItem {
  /** Stable key for React reconciliation (e.g. the recharts `dataKey`). */
  key: string;
  /** Human label shown before the value (matches the chart legend). */
  label: string;
  /** Hex or HSL colour matching the series stroke/fill. */
  color: string;
  /** Summed value for the filtered range, or `null` when the metric
   *  is an average/percentage/ratio that can't be summed. */
  value: number | null | undefined;
  /** Value formatting hint. Defaults to 'number'. */
  format?: TimelineTotalFormat;
}

export interface TimelineTotalsBadgeProps {
  items: TimelineTotalItem[];
  /** The totals are not counted yet — render a skeleton in place of every
   *  value, rather than the em-dash that means "cannot be summed". */
  loading?: boolean;
  /**
   * Optional extra classes forwarded to the wrapper — callers that need to
   * tighten spacing in very narrow card headers can drop a `gap-x-2` or
   * `text-[11px]` override here.
   */
  className?: string;
  /** data-testid for targeted Playwright / vitest queries. */
  'data-testid'?: string;
}

function formatValue(
  value: number | null | undefined,
  format: TimelineTotalFormat,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  switch (format) {
    case 'currency':
      return CURRENCY_FMT.format(value);
    case 'hours':
      return `${value.toFixed(1)}h`;
    default:
      // Round sum of counts to integers — fractional totals for count series
      // are almost always a float-accumulation artefact (0.9999999 for a
      // single `1`). Currency / hours skip this rounding because fractional
      // dollars and decimal hours are meaningful.
      return NUM_FMT.format(Math.round(value));
  }
}

export function TimelineTotalsBadge({
  items,
  loading = false,
  className,
  'data-testid': dataTestId = 'timeline-totals',
}: TimelineTotalsBadgeProps): React.ReactElement | null {
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        // `flex-wrap` keeps the block responsive on narrow cards — entries
        // wrap to a second line instead of overflowing the header.
        'flex flex-wrap items-center gap-x-3 gap-y-1 text-xs tabular-nums',
        'text-muted-foreground',
        className,
      )}
    >
      {items.map((it) => (
        <span
          key={it.key}
          data-testid={`${dataTestId}-${it.key}`}
          className="inline-flex items-center gap-1.5"
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-sm shrink-0"
            style={{ background: it.color }}
          />
          <span className="whitespace-nowrap">
            <span style={{ color: it.color }} className="font-medium">
              {it.label}:
            </span>
            {/* Literal space kept in the markup — JSX collapses whitespace
                between sibling elements, so "Sent:980" rendered without a
                gap (Briefs / Operations audit nit). The explicit `{' '}`
                node survives the JSX trim and produces "Sent: 980". */}
            {' '}
            {loading ? (
              <span
                data-testid={`${dataTestId}-${it.key}-skeleton`}
                className="inline-block h-3 w-8 rounded bg-muted animate-pulse align-middle"
              />
            ) : (
              <span className="font-semibold text-foreground">
                {formatValue(it.value, it.format ?? 'number')}
              </span>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * Sum helper — returns the numeric sum of a given field across an array of
 * rows, treating non-finite / null as 0. Shared so every chart's totals
 * computation reads the same way.
 *
 * The generic is intentionally loose (`object` rather than
 * `Record<string, unknown>`) so callers can pass strict row types from
 * `api-types.ts` (e.g. `BriefTrendPoint`, `UserTrendPoint`) without having
 * to widen them first — TS narrows `keyof T` for the caller while we still
 * do a runtime `typeof === 'number'` check before adding.
 */
export function sumField<T extends object>(
  rows: readonly T[] | null | undefined,
  field: keyof T,
): number {
  if (!rows || rows.length === 0) return 0;
  let total = 0;
  for (const r of rows) {
    const v = (r as Record<string, unknown>)[field as string];
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v;
    }
  }
  return total;
}
